import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useConfig } from '@/lib/ConfigProvider'
import { useSession } from '@/auth/SessionProvider'
import { isSameSiteWithOry } from '@/auth/ory'
import { appHost, appOrigin } from '@/lib/origin'
import { FlowForm } from '@/auth/FlowForm'
import { flowDriver } from '@/auth/flows'
import type { AnyFlow, AuthFlowKind } from '@/auth/flows'
import type { StepUp } from '@/auth/flow'
import { continuationOf } from '@/auth/continueWith'
import './AuthPage.css'

type State =
  | { readonly kind: 'starting' }
  | { readonly kind: 'ready'; readonly flow: AnyFlow }
  | { readonly kind: 'unavailable'; readonly reason: string }

interface Copy {
  readonly title: string
  readonly lede?: string
  readonly submit?: string
  readonly footer?: React.ReactNode
}

const COPY: Record<AuthFlowKind, Copy> = {
  login: {
    title: 'Sign in',
    footer: (
      <>
        <Link to="/sign-up">Create an account</Link>
        <Link to="/recover">Forgotten your password?</Link>
      </>
    ),
  },
  registration: {
    title: 'Create your account',
    // Deliberate wording. Backend §6: Ory authenticates, it never authorizes —
    // and invariant 7 makes onboarding fail-closed. An account is not
    // permission to hold money, and copy that implies otherwise sets up a
    // customer to be refused later without understanding why.
    lede: 'An account lets you sign in. Before you can hold or move money we need to verify who you are — that comes next.',
    footer: <Link to="/sign-in">I already have an account</Link>,
  },
  recovery: {
    title: 'Reset your password',
    lede: 'We will send a code to your email address.',
    footer: <Link to="/sign-in">Back to sign in</Link>,
  },
  verification: {
    title: 'Confirm your email',
    lede: 'Enter the code we sent you.',
    footer: <Link to="/sign-in">Back to sign in</Link>,
  },
}

/**
 * One page for all four unauthenticated Ory flows.
 *
 * The flows differ only in which SDK call creates and updates them and in their
 * copy; the rendering, the continuation handling and the error surfacing are
 * identical, and live in `FlowForm`.
 */
export function AuthPage({ kind }: { kind: AuthFlowKind }) {
  const config = useConfig()
  const { ory, refresh, state: session } = useSession()
  const location = useLocation()
  const [state, setState] = useState<State>({ kind: 'starting' })
  const alive = useRef(true)
  const copy = COPY[kind]

  // WHY THIS PAGE MUST SOMETIMES RENDER TO AN AUTHENTICATED CUSTOMER (#47).
  //
  // A step-up login is BY DEFINITION performed while a session is active: Ory
  // refuses a privileged settings change with `403 session_refresh_required`
  // (or `session_aal2_required`), and the session it refuses on is still
  // perfectly valid — `whoami` answers 200 throughout. So the redirect below,
  // which treats "authenticated" as "you do not need this page", sent the
  // customer straight back to the page that had just sent them here. Settings
  // remounted, asked Ory again, was refused again, and bounced again: an
  // unbounded loop with nothing on screen to explain it, and a password that
  // could never be changed.
  //
  // `stepUp` is only honoured for `login`, and only when something navigated
  // here deliberately with it. It cannot be reached by typing a URL, because it
  // travels in router state rather than in the path or the query.
  const stepUp =
    kind === 'login' ? ((location.state as { stepUp?: StepUp } | null)?.stepUp ?? null) : null

  // A step-up cannot use "a session appeared" as its completion signal, because
  // the session was already there when it started. It is complete when Ory
  // answers the update with something that is NOT another flow.
  const [stepUpDone, setStepUpDone] = useState(false)

  /**
   * What Ory said happens next, when the flow finished without a session (#60).
   *
   * Registration on a tenant that requires email verification first answers
   * with `continue_with` and no session — so the redirect below never fires and
   * `refresh()` changes nothing. Without this the form cleared its messages,
   * redrew itself unchanged, and said nothing to somebody who had in fact just
   * registered.
   */
  const [continuation, setContinuation] = useState<ReturnType<typeof continuationOf>>(null)


  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const driver = flowDriver(ory, kind, stepUp === null ? {} : { stepUp })

  const start = useCallback(async () => {
    setState({ kind: 'starting' })
    try {
      const flow = await flowDriver(ory, kind, stepUp === null ? {} : { stepUp }).create()
      if (alive.current) setState({ kind: 'ready', flow })
    } catch (err) {
      if (alive.current) {
        setState({
          kind: 'unavailable',
          reason: err instanceof Error ? err.message : String(err),
        })
      }
    }
  }, [ory, kind, stepUp])

  useEffect(() => {
    void start()
  }, [start])

  // One place that hands the browser to Ory, so FlowForm's redirect and a
  // `continue_with` redirect cannot drift apart.
  const onRedirectTo = useCallback((to: string) => {
    window.location.assign(to)
  }, [])

  const onSubmit = useCallback(
    async (body: Record<string, unknown>) => {
      if (state.kind !== 'ready') return
      const updated = await driver.update(state.flow.id, body)

      // KEEP THE FLOW ORY HANDS BACK.
      //
      // `flows.ts` asserted that settings is "the only flow whose success is a
      // 200 carrying the updated flow rather than a redirect or a session". That
      // is false, and it is false for exactly the step that matters:
      // `updateRecoveryFlow` and `updateVerificationFlow` are both typed
      // `Promise<RecoveryFlow>` / `Promise<VerificationFlow>` and resolve with
      // the updated flow at `choose_method -> sent_email`.
      //
      // Neither issues a session, so the `refresh()` below changes nothing, the
      // flow was never reassigned, and `FlowForm` re-rendered the same flow with
      // its messages already cleared. The customer pressed the button, was told
      // nothing, and never learned to go and look at their inbox — which is the
      // entire content of that step (#29).
      if (isFlow(updated)) {
        setState({ kind: 'ready', flow: updated })
      } else if (alive.current) {
        // Not a flow: this flow is finished. Usually that means a session was
        // issued — for an ordinary sign-in the redirect below fires on it
        // arriving, and for a step-up it is the only signal there is.
        setStepUpDone(true)

        // But it can finish with NO session and an instruction instead (#60).
        const next = continuationOf(updated)
        if (next?.kind === 'redirect') {
          onRedirectTo(next.to)
          return
        }
        if (next !== null) setContinuation(next)
      }

      // Registration may or may not issue a session, depending on whether the
      // tenant requires email verification first — and login always does.
      // Rather than assume either, ask who we are and let the router act on the
      // answer. `SessionProvider.refresh()` is the single place that decides.
      await refresh()
    },
    [driver, onRedirectTo, refresh, state],
  )

  const crossSite = !isSameSiteWithOry(config, appOrigin())

  // A session appearing while an unauthenticated page is open means the flow
  // completed — or that the customer already had one and navigated here by
  // hand. Either way this page is now the wrong thing to show.
  //
  // Registration lands on /welcome rather than the wallet, because creating an
  // Ory identity is not permission to hold money (invariant 7). Sending a new
  // customer to an Overview that would refuse everything is how you generate a
  // support ticket on day one.
  //
  // Placed after every hook, not at the top of the component: an early return
  // above `useEffect` would make the hook order depend on session state, which
  // React forbids and which would break exactly when the session arrives.
  if (session.status === 'authenticated' && (stepUp === null || stepUpDone)) {
    const from = (location.state as { from?: string } | null)?.from
    const to = kind === 'registration' ? '/welcome' : (from ?? '/')
    return <Navigate to={to} replace />
  }

  return (
    <main className="auth">
      <div className="auth__panel">
        <span className="auth__mark">TESORA</span>
        <h1 className="auth__title">{copy.title}</h1>
        {copy.lede !== undefined && <p className="auth__lede">{copy.lede}</p>}

        {crossSite && (
          <p className="auth__warn" role="alert">
            <strong>This deployment cannot hold a session.</strong> The identity provider is on{' '}
            <code>{hostOf(config.orySdkUrl)}</code>, a different site from <code>{appHost()}</code>,
            so its cookie is third-party and browsers discard it. The fix is for the backend to
            serve Ory under its own origin — a backend change and one config value, not a change
            here.
          </p>
        )}

        {/*
          Registered, and not signed in yet. Ory named the address it sent the
          code to; this app does not invent one, and does not claim a session
          exists (#60).
        */}
        {continuation?.kind === 'verify' && (
          <div className="auth__notice" role="status">
            <p className="auth__noticeTitle">Your account is created.</p>
            <p className="auth__muted">
              Confirm <strong>{continuation.address}</strong> before you sign in — we have sent it a
              code.
            </p>
            <Link to="/verify">Enter the code</Link>
          </div>
        )}

        {state.kind === 'starting' && <p className="auth__muted">Contacting the identity provider…</p>}

        {state.kind === 'unavailable' && (
          <div role="alert">
            <p className="auth__error">The identity provider is unreachable.</p>
            <p className="auth__muted">{state.reason}</p>
            <button type="button" className="auth__retry" onClick={() => void start()}>
              Try again
            </button>
          </div>
        )}

        {state.kind === 'ready' && (
          <FlowForm
            flow={state.flow}
            onSubmit={onSubmit}
            onContinue={(next) => setState({ kind: 'ready', flow: next })}
            onRedirect={onRedirectTo}
            {...(copy.submit === undefined ? {} : { submitLabel: copy.submit })}
          />
        )}

        {copy.footer !== undefined && <nav className="auth__links">{copy.footer}</nav>}

        <p className="auth__foot">
          Identity, credentials and recovery are handled by Ory. Tesora never sees your password.
        </p>
      </div>
    </main>
  )
}

/**
 * Whether Ory answered with a flow rather than a session or nothing.
 *
 * Structural, not a cast: `driver.update` is typed `Promise<unknown>` because
 * the five flows resolve with five different things, and a cast here would
 * assert the one shape this page happens to want.
 */
function isFlow(v: unknown): v is AnyFlow {
  return typeof v === 'object' && v !== null && 'ui' in v && 'id' in v
}

function hostOf(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}
